import React from 'react';
import CanvasJSReact from '@canvasjs/react-charts';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { ProgressSpinner } from 'primereact/progressspinner';

type StripLine = {
  startValue: number;
  endValue: number;
  color: string;
  label: string;
  labelFontColor: string;
};

type CanvasData = {
  type: string;
  name?: string;
  showInLegend?: boolean;
  dataPoints: { x: number | string | Date; y: number }[];
  xValueFormatString?: string;
};

interface LinePerTimeChartProps {
  TITLE: string;
  DATA: CanvasData[];
  MAXY?: number;
  DURATION_HOURS?: boolean;
  TARGET_LINE?: number;
}

const LinePerTimeChart: React.FC<LinePerTimeChartProps> = ({ TITLE, DATA, MAXY, DURATION_HOURS = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  let maxy = null;
  if (MAXY != null) {
    maxy = MAXY;
  }

  let miny: number | null = 0;
  if (TITLE === 'Horimetro') {
    miny = null;
  }

  const stripLines: StripLine[] = [];

  const chartOptions = {
    animationEnabled: true,
    backgroundColor: 'transparent',
    theme: isDark ? 'dark2' : 'light2',
    title: {
      text: '',
    },
    legend: {
      verticalAlign: 'top',
      reversed: false,
      cursor: 'pointer',
      fontSize: 14,
      fontFamily: 'Mukta, Saira, Roboto, sans-serif',
      fontColor: isDark ? '#f1f5f9' : '#1e293b',
    },
    axisX: {
      labelFontColor: isDark ? '#cbd5e1' : '#475569',
      lineColor: isDark ? 'rgba(255, 255, 255, 0.15)' : '#cbd5e1',
      gridColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f1f5f9',
      labelFontFamily: 'Mukta, Saira, Roboto, sans-serif',
    },
    axisY: {
      title: TITLE,
      titleFontColor: isDark ? '#f1f5f9' : '#1e293b',
      titleFontFamily: 'Mukta, Saira, Roboto, sans-serif',
      titleFontSize: 14,
      labelFontColor: isDark ? '#cbd5e1' : '#475569',
      lineColor: isDark ? 'rgba(255, 255, 255, 0.15)' : '#cbd5e1',
      gridColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0',
      labelFontFamily: 'Mukta, Saira, Roboto, sans-serif',
      minimum: miny,
      maximum: maxy,
      ...(DURATION_HOURS ? { interval: 6, suffix: "h" } : {}),
      stripLines,
    },
    toolTip: {
      shared: true,
      ...(DURATION_HOURS ? {
        contentFormatter: (e: any) => {
          const entries = e.entries || [];
          const escape = (text: string) => text.replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] || char));
          const date = entries.length ? new Date(entries[0].dataPoint.x).toLocaleDateString('pt-BR') : '';
          return [date, ...entries.map((entry: any) => {
            const value = Number(entry.dataPoint.y);
            const minutes = Math.round(Math.abs(value) * 60);
            const duration = Number.isFinite(value) ? `${value < 0 ? '-' : ''}${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m` : '—';
            return `${escape(String(entry.dataSeries.name || ''))}: ${duration}`;
          })].join('<br/>');
        },
      } : {}),
      backgroundColor: isDark ? '#162328' : '#ffffff',
      fontColor: isDark ? '#f1f5f9' : '#1e293b',
      borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : '#cbd5e1',
    },
    data: DATA,
  };

  const isLoading = false;

  return (
    <div style={{ width: '100%' }}>
      {isLoading && (
        <ProgressSpinner
          style={{
            width: '50px',
            height: '50px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: '1',
          }}
        />
      )}
      <div className="box-dash">
        <Paper elevation={5} sx={{ p: 2.5, height: '100%' }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: 'text.primary' }}>
            {TITLE}
          </Typography>
          <CanvasJSReact.CanvasJSChart options={chartOptions} />
        </Paper>
      </div>
    </div>
  );
};

export default LinePerTimeChart;
